import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, X, Trash2, Download } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import Layout from "../../components/Layout";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function SubmissionDetail({ subId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/admin/screening/submissions/${subId}`);
        if (alive) setData(r.data);
      } catch (e) { setError(formatApiError(e)); }
    })();
    return () => { alive = false; };
  }, [subId]);

  const remove = async () => {
    if (!window.confirm("Slette denne innsendingen? Kan ikke angres.")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/screening/submissions/${subId}`);
      onClose(true);
    } catch (e) { setError(formatApiError(e)); setDeleting(false); }
  };

  if (error) return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--noir)] border border-white/10 p-6 max-w-md">
        <div className="text-sm text-[var(--cream)]">{error}</div>
        <button onClick={() => onClose(false)} className="btn-ghost mt-4">Lukk</button>
      </div>
    </div>
  );
  if (!data) return null;

  const { submission, form, schema } = data;
  const answers = submission.data || {};

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-[var(--noir)] border border-white/10 max-w-3xl w-full my-8" data-testid="submission-detail">
        <div className="sticky top-0 bg-[var(--noir)] border-b border-white/10 p-4 flex items-center justify-between z-10">
          <div>
            <div className="font-meta text-xs text-[var(--cream-soft)]">#{submission.submission_number} · {fmtDate(submission.submitted_at)}</div>
            <div className="font-fraunces italic text-xl text-[var(--cream)]">{answers.navn || "(Uten navn)"}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={remove} disabled={deleting} className="btn-ghost text-xs text-[var(--magenta)]" data-testid="delete-sub">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => onClose(false)} className="btn-ghost text-xs" data-testid="close-detail">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {schema?.sections.map((section, si) => {
            const visibleFields = section.fields.filter((f) => {
              const cond = f.showIf || {};
              return Object.entries(cond).every(([k, v]) => String(answers[k] ?? "") === String(v));
            });
            const hasAnswer = visibleFields.some((f) => {
              const val = answers[f.key];
              if (f.type === "file_multi") return (submission.media || []).length > 0;
              return val !== undefined && val !== null && val !== "" && val !== false;
            });
            if (!hasAnswer) return null;
            return (
              <div key={si} className="space-y-3">
                <h3 className="font-fraunces italic text-lg text-[var(--cream)] border-b border-white/10 pb-1">{section.title}</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {visibleFields.map((f) => {
                    if (f.type === "file_multi") {
                      const items = submission.media || [];
                      if (items.length === 0) return null;
                      const openMedia = async (path) => {
                        try {
                          const r = await api.get(`/admin/screening/media?path=${encodeURIComponent(path)}`, { responseType: "blob" });
                          const url = URL.createObjectURL(r.data);
                          window.open(url, "_blank", "noopener");
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch (e) { alert(formatApiError(e)); }
                      };
                      return (
                        <div key={f.key} className="col-span-full">
                          <dt className={`font-meta text-[10px] uppercase tracking-wider text-[var(--cream-faint)] ${f.indent ? "pl-4" : ""}`}>{f.label}</dt>
                          <dd className="mt-2 flex flex-wrap gap-2">
                            {items.map((m, i) => (
                              <button key={i} type="button" onClick={() => openMedia(m.path)}
                                 className="border border-white/10 px-3 py-2 font-meta text-xs text-[var(--cream-soft)] hover:border-[var(--magenta)]"
                                 data-testid={`media-${i}`}>
                                <Download className="h-3 w-3 inline mr-1" /> Bilde {i + 1} ({Math.round((m.size || 0) / 1024)} kB)
                              </button>
                            ))}
                          </dd>
                        </div>
                      );
                    }
                    let val = answers[f.key];
                    if (val === undefined || val === null || val === "") return null;
                    if (f.type === "yesno") val = val === "yes" ? "Ja" : "Nei";
                    if (f.type === "checkbox") val = val ? "Ja" : "Nei";
                    if (f.type === "age_range" && typeof val === "object") {
                      const from = val.from ?? "?";
                      const to = val.to ?? "?";
                      val = `${from} – ${to} år`;
                    }
                    return (
                      <div key={f.key} className={f.indent ? "pl-4" : ""}>
                        <dt className="font-meta text-[10px] uppercase tracking-wider text-[var(--cream-faint)]">{f.label}</dt>
                        <dd className="font-manrope text-sm text-[var(--cream)] whitespace-pre-wrap">{String(val)}</dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            );
          })}

          <div className="border-t border-white/10 pt-4 font-meta text-[10px] text-[var(--cream-faint)] space-y-1">
            <div>IP: {submission.ip || "—"}</div>
            <div>UA: {submission.user_agent || "—"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSkjemaSvar() {
  const { formId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/admin/screening/forms/${formId}/submissions`);
      setData(r.data);
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [formId]);

  const closeDetail = (reload) => {
    setSelected(null);
    if (reload) load();
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-6 space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/skjema" className="font-meta text-[var(--cream-soft)] hover:text-[var(--cream)]" data-testid="back-list">
            <ArrowLeft className="h-4 w-4 inline mr-1" /> Skjemaer
          </Link>
        </div>

        {data && (
          <div>
            <h1 className="font-fraunces italic text-3xl text-[var(--cream)]">{data.form.title}</h1>
            <div className="font-meta text-xs text-[var(--cream-soft)] mt-1">
              {data.submissions.length} innsendinger
            </div>
          </div>
        )}

        {error && <div className="border-l-2 border-[var(--magenta)] pl-3 text-sm text-[var(--cream)] font-medium">{error}</div>}

        {data && data.submissions.length === 0 && (
          <div className="font-manrope text-sm text-[var(--cream-soft)] text-center py-10">
            Ingen svar ennå.
          </div>
        )}

        {data && data.submissions.length > 0 && (
          <div className="overflow-x-auto border border-white/10">
            <table className="min-w-full text-sm" data-testid="submissions-table">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="px-3 py-2 text-left font-meta text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">#</th>
                  <th className="px-3 py-2 text-left font-meta text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">Dato</th>
                  <th className="px-3 py-2 text-left font-meta text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">Navn</th>
                  <th className="px-3 py-2 text-left font-meta text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">Alder</th>
                  <th className="px-3 py-2 text-left font-meta text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">Snap/mail</th>
                  <th className="px-3 py-2 text-left font-meta text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">Sted</th>
                  <th className="px-3 py-2 text-left font-meta text-[10px] uppercase tracking-wider text-[var(--cream-soft)]">Bilder</th>
                </tr>
              </thead>
              <tbody>
                {data.submissions.map((s) => (
                  <tr key={s.id} onClick={() => setSelected(s.id)}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`row-${s.submission_number}`}>
                    <td className="px-3 py-2 font-manrope text-[var(--cream-soft)]">{s.submission_number}</td>
                    <td className="px-3 py-2 font-manrope text-[var(--cream-soft)]">{fmtDate(s.submitted_at)}</td>
                    <td className="px-3 py-2 font-manrope text-[var(--cream)]">{s.data?.navn || "—"}</td>
                    <td className="px-3 py-2 font-manrope text-[var(--cream-soft)]">{s.data?.alder || "—"}</td>
                    <td className="px-3 py-2 font-manrope text-[var(--cream-soft)]">{s.data?.snap_mail || "—"}</td>
                    <td className="px-3 py-2 font-manrope text-[var(--cream-soft)]">{s.data?.sted || "—"}</td>
                    <td className="px-3 py-2 font-manrope text-[var(--cream-soft)]">{(s.media || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <SubmissionDetail subId={selected} onClose={closeDetail} />}
    </Layout>
  );
}
